import React, { useMemo } from "react";
import { Permission, PermissionCategory } from "../../../../services/roles.service";
import { ACTION_ICON, TONE_TEXT, actionBucket, isDangerous } from "./permissionCatalogUi";
import { buildRequiresMap, grantWithPrerequisites, revokeWithDependents } from "./permissionPrereqs";

interface Props {
  catalog:  PermissionCategory[];
  value:    Set<string>;
  onChange: (next: Set<string>) => void;
  readOnly: boolean;
}

/** A paired permission rendered as one row: a checkbox that grants the page at
 *  all, plus a View-only / Can-edit dropdown. Both halves share `group_key` and
 *  `label`; the backend distinguishes them by `access_level`. */
interface PermissionGroup {
  key:       string;
  label:     string;
  viewCode:  string;
  editCode:  string;
  viewDesc:  string | null;
  editDesc:  string | null;
}

/** Split a category's permissions into paired groups (Settings pages) and
 *  standalone per-action codes, preserving catalog order in both. */
function partition(permissions: Permission[]): {
  groups: PermissionGroup[];
  singles: Permission[];
} {
  const byGroup = new Map<string, PermissionGroup>();
  const singles: Permission[] = [];

  for (const p of permissions) {
    if (!p.group_key || !p.access_level) {
      singles.push(p);
      continue;
    }
    const existing = byGroup.get(p.group_key) ?? {
      key: p.group_key, label: p.label,
      viewCode: "", editCode: "", viewDesc: null, editDesc: null,
    };
    if (p.access_level === "edit") {
      existing.editCode = p.code;
      existing.editDesc = p.description;
    } else {
      existing.viewCode = p.code;
      existing.viewDesc = p.description;
    }
    byGroup.set(p.group_key, existing);
  }

  // A half-defined group (only one of view/edit seeded, e.g. mid-deploy) would
  // render a dropdown that cannot express one of its options — show its codes
  // as plain checkboxes instead of a broken pair.
  const groups: PermissionGroup[] = [];
  for (const g of byGroup.values()) {
    if (g.viewCode && g.editCode) groups.push(g);
    else singles.push(...permissions.filter(p => p.group_key === g.key));
  }
  return { groups, singles };
}

export const RolePermissionChecklist: React.FC<Props> = ({ catalog, value, onChange, readOnly }) => {
  // Ticking "Manage Appointments" ticks "View Appointments" with it, and
  // unticking "View Appointments" unticks "Manage Appointments" — an action
  // without its page is a grant that can never be used (the page it lives on
  // is gated on the view code). The server applies the same rule on save.
  const requires = useMemo(() => buildRequiresMap(catalog), [catalog]);
  const labelOf = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of catalog) for (const p of c.permissions) m.set(p.code, p.label);
    return m;
  }, [catalog]);
  const grant = (codes: Set<string>, code: string) => grantWithPrerequisites(codes, code, requires);
  const revoke = (codes: Set<string>, code: string) => revokeWithDependents(codes, code, requires);

  const toggle = (code: string) => {
    if (readOnly) return;
    if (value.has(code)) {
      onChange(revoke(value, code));
      return;
    }
    if (isDangerous(code) && !window.confirm(`Grant "${code}"? This is a high-impact permission.`)) return;
    onChange(grant(value, code));
  };

  /** Page checkbox: on → view only (the safer default), off → neither half. */
  const toggleGroup = (group: PermissionGroup, enabled: boolean) => {
    if (readOnly) return;
    onChange(enabled ? grant(value, group.viewCode) : revoke(value, group.viewCode));
  };

  /** Dropdown: "edit" grants both halves so the page stays reachable — the
   *  backend treats edit as implying view, and keeping them consistent here
   *  means the saved role reads the same way it looks. */
  const setGroupLevel = (group: PermissionGroup, level: "view" | "edit") => {
    if (readOnly) return;
    const next = grant(value, group.viewCode);
    onChange(level === "edit" ? grant(next, group.editCode) : revoke(next, group.editCode));
  };

  const toggleCategory = (category: PermissionCategory, checkAll: boolean) => {
    if (readOnly) return;
    let next = new Set(value);
    const { groups, singles } = partition(category.permissions);
    for (const p of singles) {
      next = checkAll ? grant(next, p.code) : revoke(next, p.code);
    }
    // "All" on a paired category grants view, not edit — turning on a whole
    // section should not silently hand out write access to every page in it.
    for (const g of groups) {
      next = checkAll ? grant(next, g.viewCode) : revoke(next, g.viewCode);
    }
    onChange(next);
  };

  if (catalog.length === 0) {
    return <p className="text-sm text-gray-400 py-4">No permissions in the catalog yet.</p>;
  }

  return (
    <div className="space-y-3">
      {catalog.map(category => {
        const { groups, singles } = partition(category.permissions);
        // A group counts as one grantable unit, so the tally matches the rows
        // on screen rather than the underlying code count.
        const total = groups.length + singles.length;
        const checkedCount =
          groups.filter(g => value.has(g.viewCode) || value.has(g.editCode)).length +
          singles.filter(p => value.has(p.code)).length;
        const allChecked = total > 0 && checkedCount === total;

        return (
          <div key={category.key} className="border border-gray-100 rounded-xl px-4 py-3">
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm font-medium text-gray-900">{category.label}</p>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className="text-xs text-gray-400">{checkedCount}/{total}</span>
                {!readOnly && (
                  <label className="flex items-center gap-1 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      className="w-3.5 h-3.5 rounded accent-blue-500 cursor-pointer"
                      checked={allChecked}
                      onChange={() => toggleCategory(category, !allChecked)}
                    />
                    <span className="text-xs text-gray-500">All</span>
                  </label>
                )}
              </div>
            </div>

            {/* Paired pages — checkbox grants the page, dropdown sets the level */}
            {groups.length > 0 && (
              <div className="mt-3 space-y-1.5">
                {groups.map(g => {
                  const enabled = value.has(g.viewCode) || value.has(g.editCode);
                  const level: "view" | "edit" = value.has(g.editCode) ? "edit" : "view";
                  return (
                    <div key={g.key} className="flex items-center gap-2.5">
                      <label
                        className={`flex items-center gap-1.5 select-none flex-1 min-w-0 ${readOnly ? "" : "cursor-pointer"}`}
                        title={(enabled && level === "edit" ? g.editDesc : g.viewDesc) ?? g.label}
                      >
                        <input
                          type="checkbox"
                          className="w-4 h-4 rounded accent-blue-500 cursor-pointer disabled:cursor-not-allowed"
                          checked={enabled}
                          disabled={readOnly}
                          onChange={() => toggleGroup(g, !enabled)}
                        />
                        <span className="text-sm text-gray-700 truncate">{g.label}</span>
                      </label>
                      <select
                        className="text-xs rounded-md border border-gray-200 bg-white px-2 py-1 text-gray-700
                          disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed flex-shrink-0"
                        value={level}
                        disabled={readOnly || !enabled}
                        onChange={e => setGroupLevel(g, e.target.value as "view" | "edit")}
                        aria-label={`${g.label} access level`}
                      >
                        <option value="view">View only</option>
                        <option value="edit">Can edit</option>
                      </select>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Standalone per-action permissions. The description is shown,
                not just a tooltip: "View Appointments" vs "Manage Appointments"
                is not guessable from the two labels alone. */}
            {singles.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5 mt-3">
                {singles.map(p => {
                  const bucket = actionBucket(p.code);
                  const needs = (p.requires ?? []).map(c => labelOf.get(c) ?? c);
                  return (
                    <label key={p.code}
                      className={`flex items-start gap-1.5 select-none min-w-0 ${readOnly ? "" : "cursor-pointer"}`}
                    >
                      <input
                        type="checkbox"
                        className="w-4 h-4 mt-0.5 rounded accent-blue-500 cursor-pointer disabled:cursor-not-allowed flex-shrink-0"
                        checked={value.has(p.code)}
                        disabled={readOnly}
                        onChange={() => toggle(p.code)}
                      />
                      <span className={`${TONE_TEXT[bucket]} mt-0.5 flex-shrink-0`}>{ACTION_ICON[bucket]}</span>
                      <span className="min-w-0">
                        <span className="block text-sm text-gray-700">{p.label}</span>
                        {p.description && (
                          <span className="block text-xs text-gray-400 leading-snug">{p.description}</span>
                        )}
                        {needs.length > 0 && (
                          <span className="block text-[11px] text-gray-400 leading-snug">
                            Needs {needs.join(", ")}
                          </span>
                        )}
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
